import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MarqueeBar } from "./MarqueeBar";
import { ProductImage } from "../ProductImage";

// Testimonials: keep client speech, drop specific marketing-style metrics
// (e.g. "+18% ticket moyen", "−6 pts food cost") unless we can back them
// with named restaurants and verifiable cases. Sober claim tags replace
// the old percentage tags. Sophie M.'s metric is preserved — it's an
// operational claim (delivery slot + outages), not a margin promise.
const TESTIMONIALS = [
  {
    quote: "Service midi plus rapide sur les frites. La cadence tient, et les clients voient la différence dès la première commande.",
    name: "Karim B.",
    role: "Gérant, Snack — Saint-Denis (93)",
    metric: "Cadence midi tenue",
  },
  {
    quote: "Livrés tous les jeudis depuis 3 ans. Zéro rupture, zéro souci. Un partenaire, pas un fournisseur.",
    name: "Sophie M.",
    role: "Achats, Franchise — 12 sites",
    metric: "3 ans · 0 rupture",
  },
  {
    quote: "La sélection sauces a renforcé notre ticket moyen et fluidifié le service. La carte est plus lisible côté client.",
    name: "Ahmed T.",
    role: "Chef, Kebab premium — Paris 19",
    metric: "Ticket moyen renforcé",
  },
];

const BRANDS = [
  { name: "ULAS", image: "/api/static/products/ulas-frites-9-9-mm.png" },
  { name: "Lamb Weston", image: "/api/static/products/lamb-weston-crinkle-wedges.png" },
  { name: "Lutosa", image: "/api/static/products/lutosa-frites-belges-12-12-mm.jpg" },
  { name: "Colona", image: "/api/static/products/colona-algerienne-1-l.jpg" },
  { name: "Factory", image: "/api/static/products/factory-chicken-burger.png" },
  { name: "Nubi", image: "/api/static/products/nubi-tiramisu-caramel-speculoos.png" },
  { name: "Daim", image: "/api/static/products/daim-chocolate-cake-with-crunchy-caramel.png" },
  { name: "Président", image: "/api/static/products/president-professionnel-creme-liquide-18.png" },
  { name: "Ligueil", image: "/api/static/products/ligueil-tranches-de-raclette-au-lait-de-vache-pasteurise-surgelees.png" },
  { name: "Antalya", image: "/api/static/products/antalya-18-durum-30-cm.png" },
  { name: "Nawhal's", image: "/api/static/products/nawhal-s-algerienne-950-ml.png" },
  { name: "Cheesecake Factory", image: "/api/static/products/the-cheesecake-factory-bakery-classic-cheesecake.jpg" },
];

/**
 * TestimonialsCarouselLogos — auto-rotating quotes + brand logos marquee.
 */
export const TestimonialsCarouselLogos = () => {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % TESTIMONIALS.length), 5500);
    return () => clearInterval(t);
  }, []);

  const current = TESTIMONIALS[idx];

  return (
    <section
      data-testid="testimonials-carousel"
      className="relative bg-fd-bg border-t border-fd-border py-24 lg:py-32 overflow-hidden"
    >
      <div className="px-6 md:px-10 lg:px-14 max-w-[1400px] mx-auto">
        <div className="mono-label-gold mb-6 flex items-center gap-3">
          <span className="w-8 h-px bg-fd-gold"></span>
          <span>Ils travaillent avec nous</span>
        </div>

        <h2 className="display-xl text-3xl sm:text-5xl lg:text-7xl text-fd-text max-w-4xl mb-16">
          300 couverts par jour. <br />
          <span className="text-gradient-gold">Zéro temps pour chercher.</span>
        </h2>

        <div className="relative min-h-[260px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-4xl"
            >
              <p className="font-display text-2xl md:text-4xl lg:text-5xl leading-[1.1] tracking-tight">
                « {current.quote} »
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-6">
                <div>
                  <p className="font-display text-base uppercase tracking-tight">{current.name}</p>
                  <p className="mono-label !text-[10px] mt-1">{current.role}</p>
                </div>
                <span className="inline-block bg-fd-gold/15 text-fd-gold mono-label !text-[10px] !text-fd-gold px-3 py-1.5 border border-fd-gold/25">
                  {current.metric}
                </span>
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="mt-8 flex gap-2" role="tablist">
            {TESTIMONIALS.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                data-cursor-hover
                className={`h-px transition-all duration-500 ${idx === i ? "w-10 bg-fd-gold" : "w-5 bg-fd-text/20"}`}
                aria-label={`Témoignage ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Brands packshots marquee — premium image carousel of distributed brands */}
      <div className="mt-24 border-y border-fd-border py-7">
        <MarqueeBar speed="slow">
          {BRANDS.map((b, i) => (
            <div key={i} className="flex items-center px-6 shrink-0">
              <div className="fd-stage rounded-xl p-2.5 sm:p-3 lg:p-4">
                <ProductImage
                  path={b.image}
                  alt={b.name}
                  loading="lazy"
                  decoding="async"
                  className="block h-24 sm:h-28 lg:h-32 w-auto object-contain opacity-90 hover:opacity-100 transition-opacity duration-500"
                />
              </div>
              <span className="mx-8 text-fd-text/15 font-mono text-sm">✦</span>
            </div>
          ))}
        </MarqueeBar>
      </div>
    </section>
  );
};
